import type { Treatment } from '../../types/care';
import {
  LEAFLETS,
  bloodThinners,
  corticosteroidSideEffects,
  injectionResult,
  injectionTimeline,
  reportWithPacemaker,
  standardBring,
  standardComplications,
  standardInjectionFacts,
} from './shared';

export const injectionsHeadNeck: Treatment[] = [
  {
    slug: 'occipitaal-blokkade',
    name: 'Occipitaal blokkade',
    category: 'Injecties & blokkades',
    summary: 'Verdoving van de achterhoofdszenuwen bij hoofdpijnklachten.',
    intro: [
      'Een occipitaal blokkade is een behandeling waarbij een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmend medicijn (corticosteroïd) rondom de zenuw worden gespoten.',
      'De zenuwen die een rol spelen bij uw hoofdpijnklachten lopen vanuit uw nek aan beide kanten naar het achterhoofd (occipitaal). Irritatie van deze zenuwen kan hoofdpijn geven, mogelijk ook in uw voorhoofd. Deze irritatie kan ontstaan door een ongeval of een slechte houding, maar ook zonder aanwijsbare reden.',
    ],
    procedure: [
      'Tijdens de behandeling zit u op de behandeltafel. De arts plaatst een naald dicht bij de zenuw; soms ervaart u hierbij een prikkeling.',
      'De arts dient de medicatie toe en verwijdert de naald. Na ongeveer 30 minuten mag u naar huis, mits u zich goed voelt.',
    ],
    facts: { ...standardInjectionFacts },
    preparation: [bloodThinners],
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Occipitaal-blokkade-VER-FOL-018-OPE-V2.0.pdf`,
  },
  {
    slug: 'pre-empt',
    name: 'PRE-EMPT behandeling bij migraine',
    category: 'Injecties & blokkades',
    summary: 'Botox-injecties volgens het PREEMPT-protocol om chronische migraine te voorkomen.',
    intro: [
      'Deze behandeling is bedoeld voor chronische migraine. Migraine kan leiden tot hevige hoofdpijn, misselijkheid en overgevoeligheid voor licht en geluid, en kan uw dagelijks functioneren aanzienlijk beperken.',
      'Migraine kan worden veroorzaakt door een combinatie van erfelijke factoren, neurologische veranderingen en prikkels zoals stress, slaapgebrek, hormonale veranderingen, voeding of bepaalde medicijnen.',
    ],
    procedure: [
      'U ligt of zit op de behandeltafel. De specialist bespreekt met u de plaatsen waar de injecties het meest effectief zijn.',
      'U krijgt een aantal injecties met Botox (botuline-A toxine) op vaste plekken in voorhoofd, slapen, achterhoofd, nek en schouders. Dit vermindert de spanning in de hoofd- en nekspieren en remt de geleiding in kleine zenuwtakjes, waardoor migraineaanvallen minder vaak en minder hevig worden.',
      'Botox werkt lokaal en verspreidt zich niet door het hele lichaam. U kunt uw dagelijkse activiteiten direct hervatten.',
    ],
    facts: { noDriving: true, sessions: 'Effect 3 – 4 maanden' },
    preparation: [
      'Vermijd de eerste 24 uur intensieve massage of wrijven op de injectieplaatsen.',
      'Houd uw hoofdpijndagen bij in een dagboek, zodat het effect goed te volgen is.',
    ],
    report: ['Zwangerschap', 'Allergieën of eerdere reacties op Botox'],
    bring: standardBring,
    result: [
      'Na de eerste behandeling kan het enkele weken duren voordat de klachten verminderen. Botox werkt over het algemeen 3 tot 4 maanden; daarna werkt het effect vanzelf uit, zonder blijvend ongewenst effect.',
    ],
    complications: standardComplications,
    sideEffects: [
      {
        title: 'Mogelijke bijwerkingen',
        items: [
          'Tijdelijke spierzwakte',
          'Tijdelijk hangend ooglid en/of asymmetrie van de aangezichtsspieren',
          'Hoofdpijn of duizeligheid',
          'Tintelingen of een doof gevoel',
          'Pijn of blauwe plekken op de injectieplaats',
        ],
      },
    ],
    pdf: `${LEAFLETS}/PRE-EMPT-behandeling-VER-FOL-033-OPE-V1.0.pdf`,
  },
  {
    slug: 'masseter-infiltratie',
    name: 'Masseter infiltratie',
    category: 'Injecties & blokkades',
    summary: 'Botox in de kauwspier bij overbelasting, tandenknarsen en kaakklemmen.',
    intro: [
      'Deze infiltratie is bedoeld om pijnklachten van de masseter (kauwspier) op te heffen. Door de klachten kunt u moeite hebben met praten, kauwen of gapen.',
      'Overbelasting van de kaakspieren kan komen door tandenknarsen en kaakklemmen, bijvoorbeeld door slaapstoornissen, langdurige stress, alcohol, bepaalde medicatie of drugs. Dit kan slijtage van het gebit, spierpijn en hoofdpijn geven.',
    ],
    procedure: [
      'U ligt of zit op de behandeltafel. De pijnspecialist vraagt u de pijnlijke plek zo precies mogelijk aan te wijzen en markeert deze met een stift.',
      'Met Botox-injecties in de kaakspieren wordt de spierspanning verlaagd, waardoor de klachten verminderen. Botox wordt niet alleen cosmetisch, maar ook effectief in de pijnbestrijding ingezet.',
    ],
    facts: { noDriving: true, sessions: 'Effect 3 – 4 maanden' },
    report: ['Zwangerschap', 'Allergieën'],
    bring: standardBring,
    result: [
      'Na de eerste behandeling kan het enkele weken duren voordat de klachten verminderen. De werkingsduur verschilt per persoon en is gemiddeld 3 tot 4 maanden.',
    ],
    complications: standardComplications,
    sideEffects: [
      {
        title: 'Mogelijke bijwerkingen',
        items: [
          'Spierzwakte',
          'Afhangend ooglid',
          'Hoofdpijn of duizeligheid',
          'Tintelingen of een doof gevoel',
          'Pijn of blauwe plekken op de injectieplaats',
        ],
      },
    ],
    pdf: `${LEAFLETS}/Masseter-infiltratie-VER-FOL-017-OPE-V2.0.pdf`,
  },
  {
    slug: 'trapezius-infiltratie',
    name: 'Trapezius infiltratie',
    category: 'Injecties & blokkades',
    summary: 'Injectie in de monnikskapspier bij het trapeziussyndroom in nek en schouders.',
    intro: [
      'Bij een trapezius infiltratie worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) in de trapeziusspier gespoten — de grote spier van de bovenrug waarmee we het schouderblad bewegen en het hoofd recht houden.',
      'Het trapeziussyndroom kan ontstaan door een slechte houding bij bureauwerk, langdurige statische belasting, stress of een whiplash. U kunt last hebben van diepe spierpijn, stijfheid en spasmen in de nek met uitstraling naar achterhoofd en schouders, pijnlijke drukpunten (triggerpoints) en een verminderde beweeglijkheid.',
    ],
    procedure: [
      'U zit of ligt op de behandeltafel. De pijnspecialist vraagt u de pijnlijke plek aan te wijzen en markeert deze. Sommige injecties worden onder röntgendoorlichting of echografie gegeven.',
      'De naald wordt in het te behandelen gebied geplaatst, de medicatie toegediend en de naald verwijderd. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Op geleide van palpatie, echo of röntgen' },
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Trapezius-infiltratie-VER-FOL-024-OPE-V3.0.pdf`,
  },
  {
    slug: 'ganglion-stellatum',
    name: 'Blokkade ganglion stellatum',
    category: 'Injecties & blokkades',
    summary: 'Echogeleide verdoving van een zenuwknoop in de hals, onder meer bij opvliegers.',
    intro: [
      'Het ganglion stellatum is een zenuwknoop in de hals, vlak naast de adamsappel. Deze zenuwknoop heeft invloed op het deel van de hersenen dat de lichaamswarmte regelt, en op onwillekeurige processen in de arm zoals doorbloeding, zweten en pijnsignalen.',
      'Door het ganglion stellatum langdurig te verdoven met Chirocaïne wordt geprobeerd deze processen te beïnvloeden — bijvoorbeeld bij opvliegers.',
    ],
    procedure: [
      'Onder echogeleiding wordt een naald bij het ganglion stellatum geplaatst en wordt de medicatie (Chirocaïne) toegediend. De behandeling duurt 10 tot 15 minuten.',
      'Direct na de behandeling kunt u wazig zien, kan het ooglid hangen, kunt u een hese stem hebben en kan de hand warmer aanvoelen. Dit komt door de verdoving en verdwijnt binnen enkele uren. In de hals kan enkele dagen napijn optreden; hiervoor kunt u paracetamol nemen.',
    ],
    facts: { guidance: 'Echogeleid', duration: '10 – 15 minuten', noDriving: true },
    preparation: [bloodThinners],
    report: ['Gebruik van bloedverdunners', 'Allergieën'],
    bring: standardBring,
    result: [
      'Het effect is vaak direct merkbaar, maar kan ook pas na enkele dagen optreden. Bij de meeste patiënten worden de opvliegers in de dagen na de behandeling geleidelijk minder.',
      'Na een aantal weken wordt u gebeld door de verpleegkundig specialist om de behandeling te evalueren.',
    ],
    complications: [
      'Nabloeding, infectie of allergische reactie — deze treden zelden op',
      'Zeer kleine kans op een klaplong. Verschijnselen zijn hoesten, pijn op de borst of kortademigheid, tot enkele uren na de behandeling. Neem dan direct contact op met uw (huis)arts.',
    ],
    pdf: `${LEAFLETS}/Blokkade-van-het-ganglion-stellatum-VER-FOL-004-OPE-V3.0.pdf`,
  },
  {
    slug: 'supra-scapularis',
    name: 'Zenuwblokkade supra-scapularis',
    category: 'Injecties & blokkades',
    summary: 'Echogeleide blokkade van de schouderzenuw bij chronische schouderpijn.',
    intro: [
      'Bij deze behandeling worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) rondom de nervus suprascapularis gespoten. Deze zenuw loopt vanuit de hals naar de schouder en arm.',
    ],
    procedure: [
      'U zit of ligt op de behandeltafel. Met het echoapparaat wordt de juiste plek bepaald, waarna de arts een naald bij uw schouderblad plaatst, dicht bij de zenuw.',
      'De arts dient de medicatie toe en verwijdert de naald. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Echogeleid' },
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Zenuwblokkade-supra-scapularis-VER-FOL-029-OPE-V3.0.pdf`,
  },
  {
    slug: 'zenuwwortel-nek',
    name: 'Zenuwwortelblokkade bij nek-, schouder- en armklachten',
    category: 'Injecties & blokkades',
    summary: 'Gerichte injectie bij een geprikkelde zenuwwortel in de nek, onder röntgendoorlichting.',
    intro: [
      'Een zenuwwortelblokkade is een behandeling waarbij een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) rondom de zenuwwortel worden gespoten. Bij uitstralende pijn naar schouder of arm is vaak één zenuwwortel in de nek geprikkeld.',
    ],
    procedure: [
      'U ligt op uw buik. Met röntgendoorlichting wordt de plek bepaald, waarna de arts een naald aan de zijkant van de wervelkolom plaatst, dicht bij de zenuwwortel.',
      'Met contrastvloeistof wordt de positie van de naald gecontroleerd. Daarna wordt de medicatie toegediend en de naald verwijderd. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Röntgendoorlichting' },
    preparation: [bloodThinners],
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: [
      ...standardComplications,
      'Bij behandeling van de laagste halswervelzenuw (C7 of C8) is er een zeer klein risico op een klaplong',
    ],
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Zenuwwortelblokkade-bij-nek-schouder-armklachten-VER-FOL-030-OPE-V30.pdf`,
  },
  {
    slug: 'botox-infiltratie',
    name: 'Botox infiltratie',
    category: 'Injecties & blokkades',
    summary: 'Injecties met botuline toxine ter vermindering van strak gespannen, stijve spieren.',
    intro: [
      'Bij deze behandeling krijgt u één of meerdere injecties ter vermindering van strak gespannen, stijve spieren. Hierbij wordt gebruikgemaakt van botuline toxine, beter bekend als botox. Door de aangedane spieren te injecteren met botox, zal de spierspanning en hiermee de pijn verminderen.',
      'Botox kan onder andere worden toegepast bij piriformis syndroom, migraine, aangezichtspijnen, verkrampte kaakspieren (musculus masseter) en trapezius neuralgie (gespannen schouders).',
    ],
    procedure: ['De aangedane spieren worden met botox geïnjecteerd.'],
    facts: { sessions: 'Effect 3 – 6 maanden' },
    preparation: [
      'Ga op de dag van de behandeling niet naar de sauna.',
      'Masseer de plek niet en droog deze niet stevig af.',
      'Douche niet te heet, in verband met spreiding van de botox.',
    ],
    result: [
      'Botox begint 24 tot 48 uur na de toediening in te werken. Het effect is na ongeveer 14 dagen merkbaar, afhankelijk van het spiervolume.',
      'De werking van botox is tijdelijk. Na ongeveer 3 tot 6 maanden is het effect uitgewerkt; er is daardoor geen gevaar voor een blijvend ongewenst effect.',
      'Na ongeveer 8 weken wordt u gebeld door de pijnverpleegkundige om het effect te evalueren. Dan wordt besproken of een vervolgbehandeling nodig is.',
    ],
    sideEffects: [
      {
        title: 'Mogelijke bijwerkingen',
        items: ['Spierzwakte', 'Mogelijk spierpijn door compensatie van overige spiergroepen rondom het geïnfiltreerde gebied'],
      },
    ],
    pdf: 'https://excellentcareclinics.nl/wp-content/uploads/2023/11/Patientenbrief-Botox-infiltratie.pdf',
  },
];
